if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.desktopMql = window.matchMedia('(min-width: 990px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        this.productGallery = this.closest('.cc-product');
        if (this.productGallery) {
          this.scheduleProductGalleryScroll = this.scheduleProductGalleryScroll.bind(this);
          window.addEventListener('scroll', this.scheduleProductGalleryScroll, { passive: true });
          window.addEventListener('resize', this.scheduleProductGalleryScroll);
          this.scheduleProductGalleryScroll();
        }
        if (this.closest('.cc-product') && window.matchMedia('(max-width: 749px)').matches) {
          this.elements.viewer.addEventListener('wheel', this.onProductGalleryWheel.bind(this), { passive: false });
        }
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      disconnectedCallback() {
        if (!this.productGallery || !this.scheduleProductGalleryScroll) return;
        window.removeEventListener('scroll', this.scheduleProductGalleryScroll);
        window.removeEventListener('resize', this.scheduleProductGalleryScroll);
        if (this.productGalleryScrollFrame) cancelAnimationFrame(this.productGalleryScrollFrame);
      }

      scheduleProductGalleryScroll() {
        if (this.productGalleryScrollFrame) return;
        this.productGalleryScrollFrame = requestAnimationFrame(() => {
          this.productGalleryScrollFrame = null;
          this.updateProductGalleryPosition();
        });
      }

      updateProductGalleryPosition() {
        if (!this.productGallery) return;
        if (!this.desktopMql.matches) {
          this.style.transform = '';
          return;
        }

        const productRect = this.productGallery.getBoundingClientRect();
        const galleryStyles = window.getComputedStyle(this);
        const stickyTop = parseFloat(galleryStyles.top || 0);
        const startOffset = parseFloat(galleryStyles.getPropertyValue('--cc-gallery-start-offset') || 0);
        const topClearance = parseFloat(galleryStyles.getPropertyValue('--cc-gallery-top-clearance') || 0);
        const scrollRange = Math.max(productRect.height - window.innerHeight, window.innerHeight * 1.1, 1);
        const progress = Math.min(Math.max((stickyTop - productRect.top) / scrollRange, 0), 1);
        const easedProgress = progress * progress * (3 - 2 * progress);
        const maxTravel = Math.max(productRect.height - this.offsetHeight - stickyTop, 0);
        let targetOffset = startOffset + maxTravel * easedProgress;

        if (topClearance > 0) {
          const galleryRect = this.getBoundingClientRect();
          const currentTransform = galleryStyles.transform;
          let currentTranslateY = 0;

          if (currentTransform && currentTransform !== 'none') {
            currentTranslateY = new DOMMatrixReadOnly(currentTransform).m42;
          }

          const galleryTopWithoutTransform = galleryRect.top - currentTranslateY;
          targetOffset = Math.max(targetOffset, topClearance - galleryTopWithoutTransform);
        }

        targetOffset = Math.min(Math.max(targetOffset, startOffset), startOffset + maxTravel);
        this.style.transform = `translateY(${Math.round(targetOffset)}px)`;
      }

      onProductGalleryWheel(event) {
        if (!this.mql.matches || !this.elements.viewer.slider) return;
        if (this.productGalleryWheelLocked) return;

        const delta = Math.abs(event.deltaY) >= Math.abs(event.deltaX) ? event.deltaY : event.deltaX;
        if (Math.abs(delta) < 12) return;

        const slider = this.elements.viewer.slider;
        const maxScroll = slider.scrollWidth - slider.clientWidth - 2;
        const wantsNext = delta > 0;
        const atStart = slider.scrollLeft <= 2;
        const atEnd = slider.scrollLeft >= maxScroll;
        if ((wantsNext && atEnd) || (!wantsNext && atStart)) return;

        event.preventDefault();
        this.productGalleryWheelLocked = true;
        const step = this.elements.viewer.sliderItemOffset || slider.clientWidth;
        slider.scrollTo({
          left: slider.scrollLeft + (wantsNext ? step : -step),
          behavior: 'smooth',
        });
        window.setTimeout(() => {
          this.productGalleryWheelLocked = false;
        }, 450);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
